import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { arbitrageAPI } from "../services/arbitrageAPI";
import { tradeHistoryAPI } from "../services/tradeHistoryAPI";
import { walletAPI } from "../services/api";
import Sidebar from "../components/Sidebar";
import Toast from "../components/Toast";
import ArbitrageCard from "../components/ArbitrageCard";
import ProfitCard from "../components/ProfitCard";
import ExchangeList from "../components/ExchangeList";
import TradeTable from "../components/TradeTable";
import ChartCard from "../components/ChartCard";
import { BellIcon, UserIcon } from "../components/Icons";

const Arbitrage = () => {
  const ALL_TRADES_LIMIT = 500;

  const getTradeNetProfit = (trade) => {
    if (!trade || trade.status !== "completed") return 0;
    if (trade.adminProfit !== null && trade.adminProfit !== undefined) {
      return Number(trade.adminProfit || 0);
    }
    return Number(trade.maxProfit || 0) - Number(trade.estimatedFees || 0);
  };

  // Trade history state
  const [tradeHistory, setTradeHistory] = useState([]);
  const [analyticsTrades, setAnalyticsTrades] = useState([]);
  const [tradePage, setTradePage] = useState(1);
  const [tradeLimit, setTradeLimit] = useState(10);
  const [tradeTotalPages, setTradeTotalPages] = useState(1);
  const [tradeTotal, setTradeTotal] = useState(0);
  const [tradeStartDate, setTradeStartDate] = useState("");
  const [tradeEndDate, setTradeEndDate] = useState("");
  const [totalInvested, setTotalInvested] = useState(0);
  const [totalProfit, setTotalProfit] = useState(0);
  const [totalLoss, setTotalLoss] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [maxArbitrageTradesPerDay, setMaxArbitrageTradesPerDay] = useState(1);
  const [arbitrageTradesPlacedToday, setArbitrageTradesPlacedToday] =
    useState(0);
  const isArbitrageDailyLimitReached =
    arbitrageTradesPlacedToday >= maxArbitrageTradesPerDay;
  const [botLicenseActive, setBotLicenseActive] = useState(true);
  const [botLicenseFee, setBotLicenseFee] = useState(10);
  const [botLicenseWalletBalance, setBotLicenseWalletBalance] = useState(0);
  const [showBotLicenseModal, setShowBotLicenseModal] = useState(false);
  const [botLicenseLoading, setBotLicenseLoading] = useState(false);
  const [botLicenseError, setBotLicenseError] = useState("");
  const [walletBalance, setWalletBalance] = useState(0);
  /** Phased main-vs-profit: capital available for new trades (GET /wallet/trading-cap). */
  const [mainEligibleForTrading, setMainEligibleForTrading] = useState(0);
  const [profitBalanceEstimate, setProfitBalanceEstimate] = useState(0);

  const fetchTradeHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await tradeHistoryAPI.getHistory({
        page: tradePage,
        limit: tradeLimit,
        startDate: tradeStartDate,
        endDate: tradeEndDate,
      });
      setTradeHistory(res.trades || []);
      setTradeTotalPages(res.totalPages || 1);
      setTradeTotal(res.total || 0);
      setTotalInvested(res.totalInvested || 0);
      setTotalProfit(res.totalProfit || 0);
      setTotalLoss(res.totalLoss || 0);
    } catch (e) {
      setTradeHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchAnalyticsTrades = async () => {
    try {
      const res = await tradeHistoryAPI.getHistory({
        page: 1,
        limit: ALL_TRADES_LIMIT,
      });
      setAnalyticsTrades(res.trades || []);
    } catch (e) {
      setAnalyticsTrades([]);
    }
  };

  const fetchMarketPrices = async () => {
    try {
      const res = await arbitrageAPI.getMarketPrices();
      const live = Array.isArray(res?.opportunities) ? res.opportunities : [];
      if (live.length > 0) {
        setOpportunities((prev) => {
          const statusByPair = new Map(
            (prev || []).map((item) => [item.pair, Boolean(item.isActive)]),
          );
          return live.map((item) => ({
            ...item,
            isActive: statusByPair.has(item.pair)
              ? statusByPair.get(item.pair)
              : Boolean(item.isActive),
          }));
        });
      }
    } catch (e) {
      // Keep fallback opportunities if market feed fails.
    }
  };

  // Fetch trade history
  useEffect(() => {
    fetchTradeHistory();
  }, [tradePage, tradeLimit, tradeStartDate, tradeEndDate]);

  useEffect(() => {
    fetchAnalyticsTrades();
  }, []);

  useEffect(() => {
    fetchMarketPrices();
  }, []);

  useEffect(() => {
    const checkBotLicenseStatus = async () => {
      try {
        const res = await walletAPI.getBotLicenseStatus();
        const license = res?.license || {};
        const isActive = Boolean(license?.hasLicense);
        setBotLicenseActive(isActive);
        setBotLicenseFee(Number(license?.fee || 10));
        setBotLicenseWalletBalance(Number(license?.walletBalance || 0));
        setShowBotLicenseModal(!isActive);
      } catch (error) {
        setBotLicenseActive(false);
        setShowBotLicenseModal(true);
        setBotLicenseError("Unable to verify bot license. Please try again.");
      }
    };
    checkBotLicenseStatus();
  }, []);

  const refreshWalletAndCap = useCallback(async () => {
    try {
      const [w, cap] = await Promise.all([
        walletAPI.getWallet(),
        walletAPI.getTradingCap().catch(() => null),
      ]);
      const bal = Number(w?.wallet?.balance ?? 0);
      setWalletBalance(bal);
      if (cap?.success) {
        setMainEligibleForTrading(Number(cap.mainEligible ?? bal));
        setProfitBalanceEstimate(Number(cap.profitBalanceEstimate ?? 0));
        setMaxArbitrageTradesPerDay(
          Math.max(1, Number(cap.maxArbitrageTradesPerDay ?? 1)),
        );
        setArbitrageTradesPlacedToday(
          Math.max(0, Number(cap.arbitrageTradesPlacedToday ?? 0)),
        );
      } else {
        setMainEligibleForTrading(bal);
        setProfitBalanceEstimate(0);
      }
    } catch {
      setWalletBalance(0);
      setMainEligibleForTrading(0);
      setProfitBalanceEstimate(0);
    }
  }, []);

  useEffect(() => {
    refreshWalletAndCap();
  }, [refreshWalletAndCap]);

  const navigate = useNavigate();
  // Modal state for trade confirmation
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [tradeLoading, setTradeLoading] = useState(false);
  const [tradeMessage, setTradeMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const marketPollMs = 30000;
  const [isLive, setIsLive] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const intervalRef = useRef(null);

  // Arbitrage opportunities with live price differences
  const [opportunities, setOpportunities] = useState([
    {
      pair: "BTC/USDT",
      buyExchange: "Binance",
      sellExchange: "Coinbase",
      buyPrice: 43250.0,
      sellPrice: 43420.0,
      spread: 170.0,
      spreadPercent: 0.39,
      volume24h: "245.8 BTC",
      profit: "1,245.50",
      isActive: true,
    },
    {
      pair: "ETH/USDT",
      buyExchange: "Huobi",
      sellExchange: "Kraken",
      buyPrice: 2340.5,
      sellPrice: 2368.2,
      spread: 27.7,
      spreadPercent: 1.18,
      volume24h: "892.3 ETH",
      profit: "890.30",
      isActive: true,
    },
    {
      pair: "XRP/USDT",
      buyExchange: "Coinbase",
      sellExchange: "Binance",
      buyPrice: 0.615,
      sellPrice: 0.618,
      spread: 0.003,
      spreadPercent: 0.49,
      volume24h: "15,420 XRP",
      profit: "425.75",
      isActive: false,
    },
    {
      pair: "ADA/USDT",
      buyExchange: "Kraken",
      sellExchange: "Huobi",
      buyPrice: 0.485,
      sellPrice: 0.492,
      spread: 0.007,
      spreadPercent: 1.44,
      volume24h: "8,920 ADA",
      profit: "678.90",
      isActive: true,
    },
    {
      pair: "SOL/USDT",
      buyExchange: "Binance",
      sellExchange: "Coinbase",
      buyPrice: 98.2,
      sellPrice: 99.1,
      spread: 0.9,
      spreadPercent: 0.92,
      volume24h: "5,200 SOL",
      profit: "512.40",
      isActive: true,
    },
    {
      pair: "DOGE/USDT",
      buyExchange: "Huobi",
      sellExchange: "Kraken",
      buyPrice: 0.075,
      sellPrice: 0.078,
      spread: 0.003,
      spreadPercent: 4.0,
      volume24h: "1,200,000 DOGE",
      profit: "210.15",
      isActive: false,
    },
    {
      pair: "BNB/USDT",
      buyExchange: "Binance",
      sellExchange: "Coinbase",
      buyPrice: 312.5,
      sellPrice: 314.2,
      spread: 1.7,
      spreadPercent: 0.54,
      volume24h: "3,100 BNB",
      profit: "345.60",
      isActive: true,
    },
    {
      pair: "LTC/USDT",
      buyExchange: "Kraken",
      sellExchange: "Huobi",
      buyPrice: 72.1,
      sellPrice: 73.0,
      spread: 0.9,
      spreadPercent: 1.25,
      volume24h: "2,800 LTC",
      profit: "289.75",
      isActive: true,
    },
    {
      pair: "AVAX/USDT",
      buyExchange: "Binance",
      sellExchange: "Coinbase",
      buyPrice: 35.2,
      sellPrice: 36.1,
      spread: 0.9,
      spreadPercent: 2.56,
      volume24h: "4,000 AVAX",
      profit: "198.20",
      isActive: true,
    },
    {
      pair: "MATIC/USDT",
      buyExchange: "Huobi",
      sellExchange: "Kraken",
      buyPrice: 1.12,
      sellPrice: 1.15,
      spread: 0.03,
      spreadPercent: 2.68,
      volume24h: "50,000 MATIC",
      profit: "156.80",
      isActive: false,
    },
    {
      pair: "TRX/USDT",
      buyExchange: "Binance",
      sellExchange: "Coinbase",
      buyPrice: 0.098,
      sellPrice: 0.101,
      spread: 0.003,
      spreadPercent: 3.06,
      volume24h: "200,000 TRX",
      profit: "134.55",
      isActive: true,
    },
    {
      pair: "DOT/USDT",
      buyExchange: "Kraken",
      sellExchange: "Huobi",
      buyPrice: 6.2,
      sellPrice: 6.4,
      spread: 0.2,
      spreadPercent: 3.23,
      volume24h: "7,000 DOT",
      profit: "178.90",
      isActive: true,
    },
    {
      pair: "SHIB/USDT",
      buyExchange: "Binance",
      sellExchange: "Coinbase",
      buyPrice: 0.000008,
      sellPrice: 0.0000083,
      spread: 0.0000003,
      spreadPercent: 3.75,
      volume24h: "1,000,000,000 SHIB",
      profit: "99.10",
      isActive: false,
    },
    {
      pair: "UNI/USDT",
      buyExchange: "Huobi",
      sellExchange: "Kraken",
      buyPrice: 6.8,
      sellPrice: 7.0,
      spread: 0.2,
      spreadPercent: 2.94,
      volume24h: "6,000 UNI",
      profit: "120.75",
      isActive: true,
    },
    {
      pair: "LINK/USDT",
      buyExchange: "Binance",
      sellExchange: "Coinbase",
      buyPrice: 14.2,
      sellPrice: 14.5,
      spread: 0.3,
      spreadPercent: 2.11,
      volume24h: "8,000 LINK",
      profit: "142.60",
      isActive: true,
    },
    {
      pair: "ATOM/USDT",
      buyExchange: "Kraken",
      sellExchange: "Huobi",
      buyPrice: 9.1,
      sellPrice: 9.3,
      spread: 0.2,
      spreadPercent: 2.2,
      volume24h: "5,500 ATOM",
      profit: "110.30",
      isActive: true,
    },
    {
      pair: "XLM/USDT",
      buyExchange: "Binance",
      sellExchange: "Coinbase",
      buyPrice: 0.11,
      sellPrice: 0.113,
      spread: 0.003,
      spreadPercent: 2.73,
      volume24h: "120,000 XLM",
      profit: "87.45",
      isActive: false,
    },
    {
      pair: "FIL/USDT",
      buyExchange: "Huobi",
      sellExchange: "Kraken",
      buyPrice: 4.2,
      sellPrice: 4.3,
      spread: 0.1,
      spreadPercent: 2.38,
      volume24h: "3,000 FIL",
      profit: "105.90",
      isActive: true,
    },
    {
      pair: "AAVE/USDT",
      buyExchange: "Binance",
      sellExchange: "Coinbase",
      buyPrice: 88.5,
      sellPrice: 89.7,
      spread: 1.2,
      spreadPercent: 1.36,
      volume24h: "1,200 AAVE",
      profit: "98.60",
      isActive: true,
    },
    {
      pair: "SAND/USDT",
      buyExchange: "Huobi",
      sellExchange: "Kraken",
      buyPrice: 0.42,
      sellPrice: 0.44,
      spread: 0.02,
      spreadPercent: 4.76,
      volume24h: "40,000 SAND",
      profit: "76.25",
      isActive: false,
    },
  ]);

  const tradeSizingAmount = Math.max(
    0,
    Number(mainEligibleForTrading) || Number(walletBalance) || 0,
  );

  // Filtered opportunities based on current wallet balance
  const filteredOpportunities = opportunities.map((opp) => ({ ...opp }));

  // Arbitrage bots state
  const [bots, setBots] = useState([
    {
      pair: "BTC/USDT",
      status: "Active",
      uptime: "23h 45m",
      lastTrade: "2 mins ago",
      profit: "1,245.50",
      isActive: true,
      profitValue: 1245.5,
    },
    {
      pair: "ETH/BTC",
      status: "Active",
      uptime: "12h 30m",
      lastTrade: "15 mins ago",
      profit: "890.30",
      isActive: true,
      profitValue: 890.3,
    },
    {
      pair: "XRP/USDT",
      status: "Paused",
      uptime: "0h 0m",
      lastTrade: "3 hours ago",
      profit: "425.75",
      isActive: false,
      profitValue: 425.75,
    },
    {
      pair: "ADA/USDT",
      status: "Active",
      uptime: "18h 20m",
      lastTrade: "5 mins ago",
      profit: "678.90",
      isActive: true,
      profitValue: 678.9,
    },
    // Additional arbitrage bots
    {
      pair: "SOL/USDT",
      status: "Active",
      uptime: "7h 10m",
      lastTrade: "10 mins ago",
      profit: "512.40",
      isActive: true,
      profitValue: 512.4,
    },
    {
      pair: "DOGE/USDT",
      status: "Paused",
      uptime: "0h 0m",
      lastTrade: "1 hour ago",
      profit: "210.15",
      isActive: false,
      profitValue: 210.15,
    },
    {
      pair: "BNB/USDT",
      status: "Active",
      uptime: "3h 55m",
      lastTrade: "20 mins ago",
      profit: "345.60",
      isActive: true,
      profitValue: 345.6,
    },
    {
      pair: "LTC/USDT",
      status: "Active",
      uptime: "5h 25m",
      lastTrade: "8 mins ago",
      profit: "289.75",
      isActive: true,
      profitValue: 289.75,
    },
    // 10 more arbitrage bots
    {
      pair: "AVAX/USDT",
      status: "Active",
      uptime: "2h 10m",
      lastTrade: "3 mins ago",
      profit: "198.20",
      isActive: true,
      profitValue: 198.2,
    },
    {
      pair: "MATIC/USDT",
      status: "Paused",
      uptime: "0h 0m",
      lastTrade: "2 hours ago",
      profit: "156.80",
      isActive: false,
      profitValue: 156.8,
    },
    {
      pair: "TRX/USDT",
      status: "Active",
      uptime: "4h 40m",
      lastTrade: "12 mins ago",
      profit: "134.55",
      isActive: true,
      profitValue: 134.55,
    },
    {
      pair: "DOT/USDT",
      status: "Active",
      uptime: "6h 15m",
      lastTrade: "6 mins ago",
      profit: "178.90",
      isActive: true,
      profitValue: 178.9,
    },
    {
      pair: "SHIB/USDT",
      status: "Paused",
      uptime: "0h 0m",
      lastTrade: "4 hours ago",
      profit: "99.10",
      isActive: false,
      profitValue: 99.1,
    },
    {
      pair: "UNI/USDT",
      status: "Active",
      uptime: "1h 30m",
      lastTrade: "7 mins ago",
      profit: "120.75",
      isActive: true,
      profitValue: 120.75,
    },
    {
      pair: "LINK/USDT",
      status: "Active",
      uptime: "8h 5m",
      lastTrade: "9 mins ago",
      profit: "142.60",
      isActive: true,
      profitValue: 142.6,
    },
    {
      pair: "ATOM/USDT",
      status: "Active",
      uptime: "3h 20m",
      lastTrade: "11 mins ago",
      profit: "110.30",
      isActive: true,
      profitValue: 110.3,
    },
    {
      pair: "XLM/USDT",
      status: "Paused",
      uptime: "0h 0m",
      lastTrade: "5 hours ago",
      profit: "87.45",
      isActive: false,
      profitValue: 87.45,
    },
    {
      pair: "FIL/USDT",
      status: "Active",
      uptime: "2h 50m",
      lastTrade: "13 mins ago",
      profit: "105.90",
      isActive: true,
      profitValue: 105.9,
    },
    {
      pair: "AAVE/USDT",
      status: "Active",
      uptime: "4h 15m",
      lastTrade: "14 mins ago",
      profit: "98.60",
      isActive: true,
      profitValue: 98.6,
    },
    {
      pair: "SAND/USDT",
      status: "Paused",
      uptime: "0h 0m",
      lastTrade: "6 hours ago",
      profit: "76.25",
      isActive: false,
      profitValue: 76.25,
    },
  ]);

  // Exchange data from real trade history
  const exchanges = useMemo(() => {
    const exchangeMap = new Map([
      [
        "Binance",
        { name: "Binance", connected: false, balance: 0, lastSeen: 0 },
      ],
      [
        "Coinbase Pro",
        { name: "Coinbase Pro", connected: false, balance: 0, lastSeen: 0 },
      ],
      ["Kraken", { name: "Kraken", connected: false, balance: 0, lastSeen: 0 }],
      ["Huobi", { name: "Huobi", connected: false, balance: 0, lastSeen: 0 }],
      [
        "Coinbase",
        { name: "Coinbase", connected: false, balance: 0, lastSeen: 0 },
      ],
      ["OKX", { name: "OKX", connected: false, balance: 0, lastSeen: 0 }],
      ["Bybit", { name: "Bybit", connected: false, balance: 0, lastSeen: 0 }],
    ]);

    analyticsTrades.forEach((trade) => {
      const createdAtMs = new Date(trade.createdAt).getTime();
      const completedAtMs = trade.completedAt
        ? new Date(trade.completedAt).getTime()
        : createdAtMs;
      const investment = Number(trade.investment || 0);
      const realizedNet = getTradeNetProfit(trade);
      const payout = Number((investment + realizedNet).toFixed(8));

      if (!exchangeMap.has(trade.buyExchange)) {
        exchangeMap.set(trade.buyExchange, {
          name: trade.buyExchange,
          connected: false,
          balance: 0,
          lastSeen: 0,
        });
      }
      if (!exchangeMap.has(trade.sellExchange)) {
        exchangeMap.set(trade.sellExchange, {
          name: trade.sellExchange,
          connected: false,
          balance: 0,
          lastSeen: 0,
        });
      }

      const buyExchange = exchangeMap.get(trade.buyExchange);
      const sellExchange = exchangeMap.get(trade.sellExchange);
      buyExchange.lastSeen = Math.max(buyExchange.lastSeen, createdAtMs);
      sellExchange.lastSeen = Math.max(sellExchange.lastSeen, completedAtMs);

      if (trade.status === "pending" || trade.status === "executed") {
        buyExchange.balance += investment;
      }
      if (trade.status === "completed") {
        sellExchange.balance += Math.max(0, payout);
      }
    });

    return Array.from(exchangeMap.values())
      .map((exchange) => ({
        name: exchange.name,
        connected: true,
        balance: Number(Math.max(0, exchange.balance).toFixed(2)),
      }))
      .sort((a, b) => b.balance - a.balance);
  }, [analyticsTrades]);

  // Live updates effect
  useEffect(() => {
    if (!isLive) return;

    const updateData = () => {
      // Pull real market prices from backend feed.
      fetchMarketPrices();

      // Update bot profits for active bots
      setBots((prevBots) =>
        prevBots.map((bot) => {
          if (bot.isActive) {
            const profitChange = (Math.random() - 0.3) * 5; // Small profit increase bias
            const newProfitValue = Math.max(0, bot.profitValue + profitChange);
            return {
              ...bot,
              profitValue: newProfitValue,
              profit: newProfitValue.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            };
          }
          return bot;
        }),
      );

      // Do not auto-refresh history every 2s; it causes visible UI flicker/glitch.
      // History is refreshed on filter/page change and after placing a trade.

      setLastUpdate(new Date());
    };

    intervalRef.current = setInterval(updateData, marketPollMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isLive, marketPollMs]);

  const toggleLiveUpdates = () => {
    setIsLive(!isLive);
  };

  const handleBuyBotLicense = async () => {
    try {
      setBotLicenseLoading(true);
      setBotLicenseError("");
      const res = await walletAPI.purchaseBotLicense();
      const license = res?.license || {};
      setBotLicenseActive(Boolean(license?.hasLicense));
      setBotLicenseWalletBalance(Number(license?.walletBalance || 0));
      setShowBotLicenseModal(false);
      setTradeMessage("Bot license activated successfully.");
      setShowToast(true);
    } catch (err) {
      const rawMessage =
        err?.error || err?.message || "Failed to purchase bot license.";
      const isInsufficient = String(rawMessage)
        .toLowerCase()
        .includes("insufficient");
      setBotLicenseError(
        isInsufficient
          ? "Insufficient balance to buy bot license."
          : rawMessage,
      );
    } finally {
      setBotLicenseLoading(false);
    }
  };

  // Static data removed - now using live state above

  // Chart data
  const monthlyProfitData = useMemo(() => {
    const now = new Date();
    const buckets = Array.from({ length: 12 }, (_, idx) => {
      const date = new Date(now.getFullYear(), now.getMonth() - (11 - idx), 1);
      return {
        key: `${date.getFullYear()}-${date.getMonth()}`,
        label: date.toLocaleString("en-US", { month: "short" }),
        value: 0,
      };
    });
    const bucketMap = new Map(buckets.map((bucket) => [bucket.key, bucket]));

    analyticsTrades.forEach((trade) => {
      if (trade.status !== "completed") return;
      const completedAt = new Date(trade.completedAt || trade.createdAt);
      const key = `${completedAt.getFullYear()}-${completedAt.getMonth()}`;
      const bucket = bucketMap.get(key);
      if (!bucket) return;
      bucket.value += getTradeNetProfit(trade);
    });

    const points = buckets.map((bucket) => ({
      label: bucket.label,
      value: Number(bucket.value.toFixed(2)),
    }));
    return points.length >= 2
      ? points
      : [
          { label: "Start", value: 0 },
          { label: "Now", value: 0 },
        ];
  }, [analyticsTrades]);

  const cumulativeProfitData = useMemo(() => {
    let running = 0;
    const points = monthlyProfitData.map((bucket) => {
      running += Number(bucket.value || 0);
      return {
        label: bucket.label,
        value: Number(running.toFixed(2)),
      };
    });
    return points.length >= 2
      ? points
      : [
          { label: "Start", value: 0 },
          { label: "Now", value: 0 },
        ];
  }, [monthlyProfitData]);

  return (
    <div className="flex min-h-screen bg-[var(--bg-main)]">
      <Sidebar />
      <div className="flex-1 p-4 pt-16 md:p-8 md:pt-8 lg:ml-64 overflow-x-hidden">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                Live Trading
              </h1>
              <div className="h-1 w-24 bg-gradient-to-r from-[#22c55e] to-[#16a34a] rounded-full mb-4"></div>
              <p className="text-[var(--text-primary)] text-sm md:text-base">
                Real-time profitable opportunities across exchanges
              </p>
            </div>
            {/* Platform wallet — trade sizing uses main-eligible capital only (phased split). */}
            <div className="mb-6 flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-4">
                <span className="text-[var(--text-primary)] font-semibold text-lg">
                  Total wallet ($):
                </span>
                <div className="px-4 py-2 rounded-lg border border-primary/40 bg-[var(--bg-main)] text-primary font-mono tabular-nums">
                  {Number(walletBalance || 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </div>
              {/* <div className="flex flex-wrap items-center gap-4">
                <span className="text-[var(--text-primary)] font-semibold text-lg">
                  Tradable main balance ($):
                </span>
                <div className="px-4 py-2 rounded-lg border border-emerald-500/40 bg-[var(--bg-main)] text-emerald-400 w-full sm:w-56 font-mono tabular-nums">
                  {tradeSizingAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </div> */}
              {/* <p className="text-xs text-[var(--text-primary)] opacity-60 max-w-xl">
                Trade sizing uses main-eligible funds only; estimated profit wallet{" "}
                <span className="font-mono">
                  $
                  {profitBalanceEstimate.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>{" "}
                (until dedicated wallet fields ship).
              </p> */}
            </div>

            {/* Live Status Indicator */}
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center gap-2 px-4 py-2 rounded-full ${isLive ? "bg-green-500/20 border border-green-500/50" : "bg-red-500/20 border border-red-500/50"}`}
              >
                <div
                  className={`w-2 h-2 rounded-full ${isLive ? "bg-green-500 animate-pulse" : "bg-red-500"}`}
                ></div>
                <span
                  className={`text-sm font-semibold ${isLive ? "text-green-400" : "text-red-400"}`}
                >
                  {isLive ? "LIVE" : "PAUSED"}
                </span>
              </div>
              <button
                onClick={toggleLiveUpdates}
                className={`px-4 py-2 rounded-xl font-semibold transition-all ${
                  isLive
                    ? "bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/50"
                    : "bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/50"
                }`}
              >
                {isLive ? "Pause" : "Resume"}
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {!botLicenseActive && !showBotLicenseModal && (
            <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-4">
              <p className="text-red-500 text-sm font-semibold">
                Bot license is not active. Please buy the license to start
                arbitrage trading.
              </p>
            </div>
          )}

          {isArbitrageDailyLimitReached && (
            <div className="bg-yellow-500/10 border border-yellow-500/40 rounded-xl p-4">
              <p className="text-yellow-500 text-sm font-semibold">
                UTC daily limit reached: {arbitrageTradesPlacedToday} of{" "}
                {maxArbitrageTradesPerDay} arbitrage trade(s) placed today.
              </p>
            </div>
          )}

          {/* Trading opportunities (sized by wallet balance) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {filteredOpportunities.map((opp, idx) => (
              <div
                key={idx}
                className="flex flex-col justify-between rounded-2xl border border-primary/20 bg-[var(--bg-card)] p-5 shadow-sm dark:shadow-primary/10"
              >
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-xl font-bold text-[var(--text-primary)]">
                        {opp.pair}
                      </h3>
                      <p className="text-xs text-[var(--text-primary)]">
                        {opp.buyExchange} → {opp.sellExchange}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-3 py-1.5 text-xs font-semibold rounded-full ${opp.isActive ? "bg-green-500/20 text-green-400 border border-green-500/30" : "bg-gray-700/50 text-[var(--text-secondary)] border border-gray-600/30"}`}
                      >
                        {opp.isActive ? "Active" : "Paused"}
                      </span>
                      {/* Toggle Switch */}
                      <label className="ml-2 relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={opp.isActive}
                          onChange={() =>
                            setOpportunities((prev) =>
                              prev.map((o, i) =>
                                i === idx ? { ...o, isActive: !o.isActive } : o,
                              ),
                            )
                          }
                          className="sr-only peer"
                        />
                        <div className="w-10 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#22c55e] rounded-full peer peer-checked:bg-green-400 transition-all"></div>
                        <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white border border-gray-300 rounded-full transition-all peer-checked:translate-x-4 peer-checked:bg-green-500"></div>
                      </label>
                    </div>
                  </div>
                  <div className="mb-2">
                    <p className="text-xs text-[var(--text-primary)]">
                      Buy:{" "}
                      <span className="font-semibold">${opp.buyPrice}</span> |
                      Bot Sell:{" "}
                      <span className="font-semibold">${opp.sellPrice}</span>
                    </p>
                    <p className="text-xs text-[var(--text-primary)] mt-1">
                      Real Market Sell:{" "}
                      <span className="font-semibold">
                        {opp.realMarketSellPrice !== undefined &&
                        opp.realMarketSellPrice !== null
                          ? `$${Number(opp.realMarketSellPrice).toFixed(8)}`
                          : "-"}
                      </span>
                    </p>
                  </div>
                </div>
                <button
                  className="mt-4 w-full px-4 py-2 rounded-xl font-semibold bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white hover:opacity-90 transition-all border border-primary/40"
                  onClick={() => {
                    setSelectedOpportunity(opp);
                    setShowTradeModal(true);
                  }}
                  disabled={
                    !opp.isActive ||
                    isArbitrageDailyLimitReached ||
                    !botLicenseActive
                  }
                >
                  Trade
                </button>
              </div>
            ))}
          </div>

          {/* ...existing code... */}

          {/* Connected Exchanges */}
          <ExchangeList exchanges={exchanges} />

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title="Monthly Profit/Loss" data={monthlyProfitData} />
            <ChartCard
              title="Cumulative Profit Over Time"
              data={cumulativeProfitData}
            />
          </div>

          {/* Trade History - Dynamic */}
          <div className="mt-8 rounded-2xl border border-primary/20 bg-[var(--bg-card)] p-6 shadow-sm dark:shadow-primary/10">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4">
              <div>
                <h2 className="text-xl font-bold text-[var(--text-primary)] mb-2">
                  Recent Trade History
                </h2>
                <div className="flex flex-wrap gap-4 items-center">
                  <div>
                    <label className="text-[var(--text-primary)] text-sm font-semibold">
                      Start Date:
                    </label>
                    <input
                      type="date"
                      value={tradeStartDate}
                      onChange={(e) => {
                        setTradeStartDate(e.target.value);
                        setTradePage(1);
                      }}
                      className="ml-2 px-2 py-1 rounded bg-[var(--bg-main)] border border-primary/30 text-primary"
                    />
                  </div>
                  <div>
                    <label className="text-[var(--text-primary)] text-sm font-semibold">
                      End Date:
                    </label>
                    <input
                      type="date"
                      value={tradeEndDate}
                      onChange={(e) => {
                        setTradeEndDate(e.target.value);
                        setTradePage(1);
                      }}
                      className="ml-2 px-2 py-1 rounded bg-[var(--bg-main)] border border-primary/30 text-primary"
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2 md:items-end">
                <div className="flex flex-wrap gap-4">
                  <div className="text-[var(--text-primary)] text-sm">
                    Total Invested:{" "}
                    <span className="font-bold">
                      ${Number(totalInvested || 0).toFixed(2)}
                    </span>
                  </div>
                  {/* <div className="text-[var(--text-primary)] text-sm">Total Profit: <span className="font-bold text-green-400">${Number(totalProfit || 0).toFixed(2)}</span></div>
                  <div className="text-[var(--text-primary)] text-sm">Total Loss: <span className="font-bold text-red-400">${Number(totalLoss || 0).toFixed(2)}</span></div> */}
                </div>
                <div className="flex gap-2 mt-2">
                  <label className="text-[var(--text-primary)] text-sm">
                    Rows:
                  </label>
                  <select
                    value={tradeLimit}
                    onChange={(e) => {
                      setTradeLimit(Number(e.target.value));
                      setTradePage(1);
                    }}
                    className="px-2 py-1 rounded bg-[var(--bg-main)] border border-primary/30 text-primary"
                  >
                    {[10, 20, 50].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-[900px] w-full text-sm text-[var(--text-primary)]">
                <thead>
                  <tr className="border-b border-primary/20">
                    <th className="py-2 px-3 text-left">Date</th>
                    <th className="py-2 px-3 text-left">Pair</th>
                    <th className="py-2 px-3 text-left">Buy</th>
                    <th className="py-2 px-3 text-left">Sell</th>
                    <th className="py-2 px-3 text-left">Invested</th>
                    <th className="py-2 px-3 text-left">Net P/L</th>
                    <th className="py-2 px-3 text-left">Status</th>
                    <th className="py-2 px-3 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingHistory ? (
                    <tr>
                      <td colSpan={9} className="text-center py-6">
                        Loading...
                      </td>
                    </tr>
                  ) : tradeHistory.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-6">
                        No trades found.
                      </td>
                    </tr>
                  ) : (
                    tradeHistory.map((trade) => (
                      <tr
                        key={trade.id}
                        className="border-b border-primary/10 transition-all hover:bg-surface-softer"
                      >
                        <td className="py-2 px-3">
                          {new Date(trade.createdAt).toLocaleString()}
                        </td>
                        <td className="py-2 px-3">{trade.pair}</td>
                        <td className="py-2 px-3">{trade.buyExchange}</td>
                        <td className="py-2 px-3">{trade.sellExchange}</td>
                        <td className="py-2 px-3">
                          ${trade.investment.toLocaleString()}
                        </td>
                        <td
                          className={`py-2 px-3 ${Number(trade.realizedNetProfit || 0) >= 0 ? "text-green-400" : "text-red-400"}`}
                        >
                          {trade.status === "completed"
                            ? `${Number(trade.realizedNetProfit || 0) >= 0 ? "+" : "-"}$${Math.abs(Number(trade.realizedNetProfit || 0)).toLocaleString()}`
                            : "-"}
                        </td>
                        <td className="py-2 px-3">
                          <span
                            className={`px-2 py-1 rounded font-semibold ${trade.status === "completed" ? "bg-green-500/20 text-green-400" : trade.status === "pending" ? "bg-yellow-500/20 text-yellow-400" : "bg-gray-700/40 text-gray-300"}`}
                          >
                            {trade.status}
                          </span>
                        </td>
                        <td className="py-2 px-3">
                          {trade.status === "pending" ||
                          trade.status === "executed" ? (
                            <span className="px-3 py-1 rounded font-semibold text-xs bg-blue-500/20 text-blue-300 border border-blue-500/30">
                              Auto close in 24h
                            </span>
                          ) : (
                            <span className="px-3 py-1 rounded font-semibold text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                              Closed
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="md:hidden space-y-3">
              {loadingHistory ? (
                <div className="text-center py-6 text-[var(--text-primary)]">
                  Loading...
                </div>
              ) : tradeHistory.length === 0 ? (
                <div className="text-center py-6 text-[var(--text-primary)]">
                  No trades found.
                </div>
              ) : (
                tradeHistory.map((trade) => (
                  <div
                    key={trade.id}
                    className="rounded-xl border border-primary/20 bg-surface-softer p-4"
                  >
                    <div className="mb-2 text-xs text-[var(--text-secondary)]">
                      {new Date(trade.createdAt).toLocaleString()}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm text-[var(--text-primary)]">
                      <span className="font-semibold">Pair</span>
                      <span>{trade.pair}</span>
                      <span className="font-semibold">Buy</span>
                      <span>{trade.buyExchange}</span>
                      <span className="font-semibold">Sell</span>
                      <span>{trade.sellExchange}</span>
                      <span className="font-semibold">Invested</span>
                      <span>${trade.investment.toLocaleString()}</span>
                      <span className="font-semibold">Net P/L</span>
                      <span
                        className={
                          Number(trade.realizedNetProfit || 0) >= 0
                            ? "text-green-400"
                            : "text-red-400"
                        }
                      >
                        {trade.status === "completed"
                          ? `${Number(trade.realizedNetProfit || 0) >= 0 ? "+" : "-"}$${Math.abs(Number(trade.realizedNetProfit || 0)).toLocaleString()}`
                          : "-"}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span
                        className={`px-2 py-1 rounded text-xs font-semibold ${trade.status === "completed" ? "bg-green-500/20 text-green-400" : trade.status === "pending" ? "bg-yellow-500/20 text-yellow-400" : "bg-gray-700/40 text-gray-300"}`}
                      >
                        {trade.status}
                      </span>
                      {trade.status === "pending" ||
                      trade.status === "executed" ? (
                        <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-500/20 text-blue-300 border border-blue-500/30">
                          Auto close in 24h
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                          Closed
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            {/* Pagination */}
            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 mt-4">
              <div className="text-[var(--text-primary)] text-xs">
                Page {tradePage} of {tradeTotalPages} ({tradeTotal} trades)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setTradePage((p) => Math.max(1, p - 1))}
                  disabled={tradePage === 1}
                  className="rounded border border-primary/20 bg-surface-softer px-3 py-1 font-semibold text-[var(--text-primary)] disabled:opacity-40"
                >
                  Prev
                </button>
                <button
                  onClick={() =>
                    setTradePage((p) => Math.min(tradeTotalPages, p + 1))
                  }
                  disabled={tradePage === tradeTotalPages}
                  className="rounded border border-primary/20 bg-surface-softer px-3 py-1 font-semibold text-[var(--text-primary)] disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showBotLicenseModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-overlay p-4">
          <div className="w-full max-w-lg rounded-2xl border border-primary/30 bg-[var(--bg-card)] p-6 md:p-8">
            <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-3">
              Bot License Required
            </h2>
            <p className="text-[var(--text-primary)] text-sm md:text-base mb-4">
              You need an active bot license to use arbitrage trading.
            </p>
            <div className="bg-surface-soft rounded-xl p-4 border border-primary/20 space-y-2 mb-4">
              <div className="flex items-center justify-between text-[var(--text-primary)]">
                <span>License Fee:</span>
                <span className="font-semibold">
                  ${Number(botLicenseFee).toFixed(2)}
                </span>
              </div>
              <div className="flex items-center justify-between text-[var(--text-primary)]">
                <span>Your Wallet Balance:</span>
                <span className="font-semibold">
                  ${Number(botLicenseWalletBalance).toFixed(2)}
                </span>
              </div>
            </div>
            {botLicenseError && (
              <div className="mb-4 text-sm font-semibold text-red-400">
                {botLicenseError}
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                className="flex-1 py-3 rounded-xl font-bold bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white hover:opacity-90 transition-all border border-primary/40 disabled:opacity-50"
                onClick={handleBuyBotLicense}
                disabled={botLicenseLoading}
              >
                {botLicenseLoading ? "Processing..." : "Buy Now"}
              </button>
              <button
                className="flex-1 rounded-xl border border-primary/20 bg-surface-softer py-3 font-semibold text-[var(--text-primary)] transition-all hover:bg-surface-soft"
                onClick={() => setShowBotLicenseModal(false)}
                disabled={botLicenseLoading}
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      )}
      {showTradeModal && selectedOpportunity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4">
          <div className="relative w-full max-w-md rounded-2xl border border-primary/30 bg-[var(--bg-card)] p-6 md:p-8">
            <button
              className="absolute top-3 right-3 text-[var(--text-secondary)] hover:text-primary text-2xl"
              onClick={() => {
                setShowTradeModal(false);
                setTradeMessage("");
              }}
              aria-label="Close"
            >
              &times;
            </button>
            <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-4">
              Confirm Trade
            </h2>
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-[var(--text-primary)]">
                <span>Pair:</span> <span>{selectedOpportunity.pair}</span>
              </div>
              <div className="flex justify-between text-[var(--text-primary)]">
                <span>Buy Exchange:</span>{" "}
                <span>{selectedOpportunity.buyExchange}</span>
              </div>
              <div className="flex justify-between text-[var(--text-primary)]">
                <span>Sell Exchange:</span>{" "}
                <span>{selectedOpportunity.sellExchange}</span>
              </div>
              <div className="flex justify-between text-[var(--text-primary)]">
                <span>Tradable main balance ($):</span>{" "}
                <span>
                  {tradeSizingAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
            {tradeMessage && (
              <div className="mb-2 text-center text-sm font-semibold text-green-500">
                {tradeMessage}
              </div>
            )}
            <button
              className="w-full py-3 rounded-xl font-bold bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white hover:opacity-90 transition-all border border-primary/40 mt-2"
              disabled={tradeLoading}
              onClick={async () => {
                setTradeLoading(true);
                setTradeMessage("");
                try {
                  await arbitrageAPI.placeTrade({
                    pair: selectedOpportunity.pair,
                    buyExchange: selectedOpportunity.buyExchange,
                    sellExchange: selectedOpportunity.sellExchange,
                    investment: tradeSizingAmount,
                  });
                  setTradeMessage("Trade placed successfully!");
                  await refreshWalletAndCap();
                  await fetchTradeHistory();
                  await fetchAnalyticsTrades();
                  setShowToast(true);
                  setTimeout(() => {
                    setShowTradeModal(false);
                    setTradeMessage("");
                    setShowToast(false);
                  }, 2000);
                } catch (err) {
                  setTradeMessage(err?.error || "Failed to place trade.");
                  if (
                    String(err?.error || "")
                      .toLowerCase()
                      .includes("per utc day")
                  ) {
                    await refreshWalletAndCap();
                  }
                } finally {
                  setTradeLoading(false);
                }
              }}
            >
              {tradeLoading ? "Placing Trade..." : "Confirm & Place Trade"}
            </button>
          </div>
        </div>
      )}
      <Toast
        message={tradeMessage}
        show={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
};

export default Arbitrage;
